import { QueryClient, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import {
  DeleteLeadsMutation,
  DeleteLeadsMutationVariables,
  LeadsQuery,
} from "lib/generated/app-service-gql/graphql";
import { graphql } from "../../../generated/app-service-gql";
import { useGraphQlMutation } from "../useGraphQlClient";

const DELETE_LEADS_MUTATION = graphql(`
  mutation deleteLeads($ids: [String!]!) {
    deleteLeads(ids: $ids) {
      id
    }
  }
`);

export function useDeleteLeads(
  options: UseMutationOptions<DeleteLeadsMutation, unknown, DeleteLeadsMutationVariables, unknown>,
) {
  const queryClient = useQueryClient();
  const { data, ...rest } = useGraphQlMutation(DELETE_LEADS_MUTATION, {
    ...options,
    onSuccess: (data, variables, context) => {
      for (const id of variables.ids) {
        // No need to await this, deletion takes too long and waiting isn't critical
        void queryClient.invalidateQueries({
          queryKey: ["lead", id],
        });
      }

      // TODO: Check at some point that this would be ok when useInfiniteLeads query is used
      const deletedIds = data.deleteLeads.map((l) => l.id);
      removeDeletedLeadsFromCache(queryClient, deletedIds);

      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data, ...rest };
}

/**
 * Removes any leads in the cache assigned to any of the deleted leads
 * @param queryClient
 * @param deletedIds
 */
function removeDeletedLeadsFromCache(queryClient: QueryClient, deletedIds: string[]) {
  const allLeadsQueries = queryClient.getQueryCache().findAll({ queryKey: ["leads"] });

  for (const leadsQuery of allLeadsQueries) {
    queryClient.setQueryData(leadsQuery.queryKey, (prevData: LeadsQuery | undefined) => {
      if (!prevData) return prevData;
      const removedLeads = prevData.leads.results.filter((o) => deletedIds.includes(o.id));
      const updatedResults = prevData.leads.results.filter((o) => !deletedIds.includes(o.id));
      const removedValue = removedLeads.reduce((sum, lead) => sum + (lead.value ?? 0), 0);
      return {
        leads: {
          ...prevData.leads,
          results: updatedResults,
          total: prevData.leads.total - removedLeads.length,
          valueSum: prevData.leads.valueSum - removedValue,
        },
      };
    });
  }
}
